import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { MoonIcon, SunIcon } from 'lucide-react';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Switch,
  ChevronRightIcon,
} from '@noor/ui';
import { cn } from '@noor/lib/cn';
import {
  useUiStore,
  withPageCrossfade,
  LANGUAGES,
  LANGUAGE_LABEL,
  type Language,
} from '@noor/lib';
import { SignOutButton } from '@/features/sign-out';
import { useLegacyProfile } from '@/entities/legacy-profile';
import { legacyFileUrl } from '@/entities/legacy-order';

/**
 * The header avatar menu (Доставка shell). Clicking the avatar opens a popover
 * with the theme toggle and language switcher, and a Profile link to `/profile`
 * pinned at the bottom — it no longer navigates straight to the profile page.
 * This is also the only path to theme/language on mobile, where the standalone
 * header controls are hidden. Twin of the Eats shell's account menu.
 */
export function DeliveryAccountMenu() {
  const { t, i18n } = useTranslation('delivery');
  const [open, setOpen] = useState(false);

  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);

  const isDark = theme === 'dark';

  const changeLanguage = (lng: Language) => {
    setLanguage(lng);
    void i18n.changeLanguage(lng);
  };

  // The merchant's own logo from `GET /vendors/me` — the same source the
  // profile card reads. `legacyFileUrl` prefixes storage-relative paths with
  // the file base URL and passes absolute URLs through untouched.
  const { data: profile } = useLegacyProfile();
  const logoUrl = legacyFileUrl(profile?.business_logo) ?? undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('utilities.account')}
          className="cursor-pointer rounded-full transition-transform duration-200 ease-out outline-none hover:scale-105 focus-visible:ring-[3px] focus-visible:ring-ring/40 active:scale-95"
        >
          <Avatar className="size-14 border border-muted">
            <AvatarImage src={logoUrl} alt="" />
            <AvatarFallback>NR</AvatarFallback>
          </Avatar>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[260px] gap-3 rounded-[16px] border-0 p-[10px] shadow-popover-halo"
      >
        <div className="flex flex-col gap-1">
          {/* Theme toggle — the Switch is the interactive control */}
          <label className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-[12px] px-3 transition-colors hover:bg-muted has-[:focus-visible]:bg-muted">
            <span className="relative flex size-5 shrink-0 items-center justify-center text-foreground">
              <SunIcon className="size-[18px] scale-100 rotate-0 transition-all duration-300 ease-out dark:scale-0 dark:-rotate-90" />
              <MoonIcon className="absolute size-[18px] scale-0 rotate-90 transition-all duration-300 ease-out dark:scale-100 dark:rotate-0" />
            </span>
            <span className="flex-1 text-left text-sm font-medium tracking-tight text-foreground">
              {isDark ? t('utilities.themeDark') : t('utilities.themeLight')}
            </span>
            <Switch
              checked={isDark}
              onCheckedChange={() => withPageCrossfade(toggleTheme)}
              aria-label={t('utilities.theme')}
            />
          </label>

          {/* Language switcher */}
          <div className="flex items-center gap-3 px-3 py-2">
            <span className="flex-1 text-sm font-medium tracking-tight text-foreground">
              {t('utilities.language')}
            </span>
            <div className="flex items-center gap-1 rounded-full bg-muted p-1">
              {LANGUAGES.map((lng) => {
                const isActive = lng === language;
                return (
                  <button
                    key={lng}
                    type="button"
                    aria-pressed={isActive}
                    aria-label={t(`utilities.languages.${lng}`)}
                    onClick={() => changeLanguage(lng)}
                    className={cn(
                      'flex h-7 min-w-8 items-center justify-center rounded-full px-2 text-xs font-semibold tracking-tight',
                      'cursor-pointer transition-colors outline-none',
                      'focus-visible:ring-[3px] focus-visible:ring-ring/40',
                      isActive
                        ? 'bg-card text-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {LANGUAGE_LABEL[lng]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Profile link */}
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className={cn(
              'flex h-11 w-full items-center gap-3 rounded-[12px] px-3',
              'text-sm font-medium tracking-tight text-foreground',
              'transition-colors outline-none hover:bg-muted focus-visible:bg-muted',
            )}
          >
            <Avatar className="size-6 shrink-0">
              <AvatarImage src={logoUrl} alt="" />
              <AvatarFallback className="text-[10px]">NR</AvatarFallback>
            </Avatar>
            <span className="flex-1">{t('utilities.profile')}</span>
            <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
          </Link>

          {/* Sign out — ends the session and returns to /login */}
          <SignOutButton onBeforeConfirm={() => setOpen(false)} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
